import { useState } from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Send, Loader2, CircleCheck } from "lucide-react";
import { api } from "@/lib/api";

const SOURCE_STYLES = {
  Google: "bg-[#4285F4]/10 text-[#4285F4]",
  Airbnb: "bg-[#FF5A5F]/10 text-[#FF5A5F]",
  Trustpilot: "bg-[#00B67A]/10 text-[#00B67A]",
  Booking: "bg-[#003580]/10 text-[#003580]",
  Site: "bg-clay/10 text-clay",
};

const Stars = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} size={14} className={s <= rating ? "fill-clay text-clay" : "text-line"} />
    ))}
  </div>
);

const ReviewCard = ({ review, i }) => (
  <article
    data-testid={`review-card-${i}`}
    className="mx-3 flex w-[320px] shrink-0 flex-col gap-4 rounded-2xl border border-line bg-white p-6 shadow-[0_10px_30px_-18px_rgba(28,27,26,0.15)] sm:w-[380px]"
  >
    <div className="flex items-center justify-between">
      <Stars rating={review.rating} />
      <span className={`rounded-full px-3 py-1 text-[11px] font-medium ${SOURCE_STYLES[review.source] || "bg-sand text-fog"}`}>
        {review.source === "Site" ? "Hóspede" : review.source}
      </span>
    </div>
    <p className="text-sm leading-relaxed text-ink/80">“{review.text}”</p>
    <div className="mt-auto flex items-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sand font-display text-sm text-clay">
        {review.author.charAt(0)}
      </span>
      <span className="text-sm font-medium text-ink">{review.author}</span>
    </div>
  </article>
);

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 focus:border-clay placeholder:text-fog/60";

const ReviewForm = () => {
  const [form, setForm] = useState({ author: "", rating: 5, text: "" });
  const [hover, setHover] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.author.trim().length < 2) return toast.error("Indique o seu nome.");
    if (form.text.trim().length < 10) return toast.error("Escreva um comentário um pouco mais longo.");
    setSending(true);
    try {
      await api.post("/reviews/public", form);
      setDone(true);
      toast.success("Obrigado! O seu comentário será publicado após revisão.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar o comentário.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-white p-8 text-center" data-testid="review-success">
        <CircleCheck size={36} className="text-clay" />
        <p className="font-display text-xl text-ink">Comentário recebido!</p>
        <p className="max-w-xs text-sm text-fog">Obrigado por partilhar a sua experiência. Será publicado assim que for revisto.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-2xl border border-line bg-white p-6 sm:p-8" data-testid="review-form">
      <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <label className="flex flex-col gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-fog">
          O seu nome
          <input data-testid="public-review-author" placeholder="Ex.: Maria S." className={inputCls} value={form.author}
            onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} />
        </label>
        <div className="flex flex-col gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-fog">
          Classificação
          <div className="flex gap-1 py-2" data-testid="public-review-rating" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} type="button" data-testid={`public-review-star-${s}`} onMouseEnter={() => setHover(s)}
                onClick={() => setForm((f) => ({ ...f, rating: s }))} className="transition-transform duration-200 hover:scale-110">
                <Star size={24} className={s <= (hover || form.rating) ? "fill-clay text-clay" : "text-line"} />
              </button>
            ))}
          </div>
        </div>
      </div>
      <label className="flex flex-col gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-fog">
        O seu comentário
        <textarea data-testid="public-review-text" rows={4} placeholder="Como foi a sua estadia?" className={`${inputCls} resize-none`}
          value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} />
      </label>
      <button data-testid="public-review-submit" type="submit" disabled={sending}
        className="inline-flex w-fit items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-sm font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.02] hover:bg-clay-dark disabled:opacity-60">
        {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        {sending ? "A enviar..." : "Enviar comentário"}
      </button>
      <p className="text-xs text-fog">Os comentários são revistos antes de serem publicados.</p>
    </form>
  );
};

export const Reviews = ({ reviews }) => (
  <section id="comentarios" data-testid="reviews-section" className="overflow-hidden py-24 md:py-32">
    <div className="mx-auto mb-12 max-w-7xl px-5 sm:px-8">
      <p className="mb-4 text-xs font-medium uppercase tracking-[0.3em] text-clay">Comentários</p>
      <h2 className="max-w-2xl font-display text-4xl leading-tight text-ink sm:text-5xl">
        {reviews.length ? <>Quem cá esteve, <em className="text-clay">quer voltar</em>.</> : <>Já ficou connosco? <em className="text-clay">Conte-nos</em>.</>}
      </h2>
    </div>
    {reviews.length > 0 && (
      <div className="mb-16" data-testid="reviews-marquee">
        <Marquee speed={28} gradient={false} pauseOnHover>
          {reviews.map((r, i) => (
            <ReviewCard key={r.id || i} review={r} i={i} />
          ))}
        </Marquee>
      </div>
    )}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.2fr]"
    >
      <div>
        <h3 className="font-display text-2xl text-ink sm:text-3xl">Deixe o seu comentário</h3>
        <p className="mt-4 max-w-sm text-base leading-relaxed text-fog">
          A sua opinião ajuda outros hóspedes a escolher e ajuda-nos a melhorar. Leva menos de um minuto.
        </p>
      </div>
      <ReviewForm />
    </motion.div>
  </section>
);
